import { useMutation, useQueryClient } from 'react-query';

import {
  WhatsappTemplateConfigResponse,
  WhatsappTemplatesResponse,
} from '@bandido/shared/types';
import { client } from '@frontend/features/common/services';
import { QueryKeys } from '@frontend/features/common/types';

type UpdateTemplateConfigPayload = {
  templateId: string;
  placeholderMappings: string[];
};

export const useUpdateTemplateConfig = (agencyId?: number) => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({
      templateId,
      placeholderMappings,
    }: UpdateTemplateConfigPayload): Promise<WhatsappTemplateConfigResponse> => {
      if (!agencyId) {
        throw new Error('Agency ID is required');
      }

      const { data } = await client.patch(
        `/whatsapp/templates/${agencyId}/${templateId}/config`,
        { placeholderMappings },
      );

      return data;
    },
    {
      onMutate: async ({ templateId, placeholderMappings }) => {
        if (!agencyId) return undefined;

        await queryClient.cancelQueries([QueryKeys.gupshupTemplates, agencyId]);

        const previous = queryClient.getQueryData<WhatsappTemplatesResponse>([
          QueryKeys.gupshupTemplates,
          agencyId,
        ]);

        if (previous) {
          queryClient.setQueryData<WhatsappTemplatesResponse>(
            [QueryKeys.gupshupTemplates, agencyId],
            {
              ...previous,
              templates: previous.templates.map((template) =>
                template.id === templateId
                  ? { ...template, placeholderMappings }
                  : template,
              ),
            },
          );
        }

        return { previous };
      },
      onError: (_error, _variables, context) => {
        if (!agencyId) return;

        if (context?.previous) {
          queryClient.setQueryData(
            [QueryKeys.gupshupTemplates, agencyId],
            context.previous,
          );
        }
      },
      onSettled: () => {
        if (!agencyId) return;

        queryClient.invalidateQueries([QueryKeys.gupshupTemplates, agencyId]);
      },
    },
  );
};
